/**
 * src/pages/ClienteDetalle.jsx
 * Vista detalle de un cliente: info, empresas propias, competidores, scheduler.
 */
import { Fragment, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { BarraPestanas } from '../components/ui/Pestanas'
import { EjecucionesCliente } from '../components/cliente/EjecucionesCliente'
import { DiagnosticoCliente } from '../components/cliente/DiagnosticoCliente'
import { TiendasDelTenant } from '../components/cliente/TiendasDelTenant'
import { Card } from '../components/ui/Card'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input, Toggle, Select } from '../components/ui/Input'
import { Table } from '../components/ui/Table'
import { Pista } from '../components/ui/Pista'
import { BarraTramos } from '../components/ui/BarraTramos'
import { clp, pct as _pct } from '../lib/cifras'
import {
  useCliente, useUso, useUpdateCliente, useUpdateCompetidor, useUpdateEmpresa,
  useUpdateScheduler, useAddCompetidor,
  useDeleteCliente, useUpdateTierOverride, useUpdateEstado, useCancelarSuscripcion,
} from '../hooks/useClientes'

const TIER_OPTS = [
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
  { value: 'enterprise', label: 'Enterprise' },
]
const _fmtFecha = (iso) => fechaCL(iso)
// Valor por defecto seguro para la baja programada (fin del mes en curso), sin
// depender de la suscripción. El admin lo ajusta; sirve para que el campo nunca
// quede en blanco cuando se activa "Programar baja".
const _finDeMes = () => {
  const d = new Date()
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const mm = String(last.getMonth() + 1).padStart(2, '0')
  const dd = String(last.getDate()).padStart(2, '0')
  return `${last.getFullYear()}-${mm}-${dd}`
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000'
import { useFichaCliente } from '../hooks/useAdminPanel'
import api from '../api/client'
import { fechaCL, fechaHoraCL } from '../lib/horaChile'

const FREQ_OPTS = [
  { value: '24h', label: 'Cada 24h' },
  { value: '48h', label: 'Cada 48h' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'manual', label: 'Manual' },
]

// (El TabBtn que vivía aquí murió con las pestañas de la fase f90e748 y quedó
// huérfano; el patrón vive ahora en components/ui/Pestanas.jsx.)

/**
 * Contacto y usuarios, en una sola línea (Manuel, 28-08). Nace de la avería del
 * correo semanal: la ficha enseñaba el correo de contacto pero no decía quién
 * recibe de verdad el resumen (los USUARIOS del dashboard), ni había dónde
 * introducir un correo cuando se le pide al cliente. Ahora: el contacto se
 * edita aquí (y es el RESPALDO del resumen si no hay usuarios), y el alta de
 * usuarios —que ya existía en /usuarios/crear del backend— gana su puerta.
 */
function ContactoUsuarios({ cliente, ficha, clienteId }) {
  const update = useUpdateCliente(clienteId)
  const [editando, setEditando] = useState(false)
  const [correo, setCorreo] = useState(cliente.email || '')
  const usuarios = ficha?.usuarios

  const guardar = () => {
    update.mutate({ email: correo.trim() }, { onSuccess: () => setEditando(false) })
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap',
      marginTop: 'var(--space-4)', padding: 'var(--space-2) var(--space-3)',
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-sm)', color: 'var(--color-text-2)',
    }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
                     letterSpacing: '0.11em', textTransform: 'uppercase',
                     color: 'var(--color-text-3)' }}>
        Contacto
      </span>
      {editando ? (
        <>
          <input
            value={correo}
            onChange={e => setCorreo(e.target.value)}
            placeholder="correo de contacto"
            style={{
              padding: '3px 8px', fontSize: 'var(--text-sm)', width: '240px',
              fontFamily: 'var(--font-mono)', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border-2)',
              background: 'var(--color-surface-2)', color: 'var(--color-text)',
            }}
          />
          <Button size="sm" onClick={guardar} loading={update.isPending}>Guardar</Button>
          <Button size="sm" variant="secondary"
                  onClick={() => { setEditando(false); setCorreo(cliente.email || '') }}>
            Cancelar
          </Button>
        </>
      ) : (
        <>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>
            {cliente.email || 'sin correo de contacto'}
          </span>
          <button
            onClick={() => setEditando(true)}
            title="Editar el correo de contacto"
            style={{ background: 'none', border: 'none', cursor: 'pointer',
                     color: 'var(--color-text-3)', fontSize: 'var(--text-sm)', padding: 0 }}
          >
            ✎
          </button>
        </>
      )}
      <span style={{ color: 'var(--color-border-2)' }}>·</span>
      <span>
        Usuarios del dashboard: {usuarios ? usuarios.n : '—'}
        {usuarios && ` (${usuarios.reciben_resumen} recibe${usuarios.reciben_resumen === 1 ? '' : 'n'} el resumen)`}
      </span>
      {usuarios && usuarios.reciben_resumen === 0 && (
        <span style={{ color: 'var(--color-warn)', fontFamily: 'var(--font-mono)',
                       fontSize: 'var(--text-xs)' }}>
          ⚠ el resumen semanal cae al correo de contacto
        </span>
      )}
      <button
        // El router de usuarios se monta con prefijo /admin (main.py): sin él,
        // el formulario devuelve 404 — lo cazó Manuel en producción el 28-08.
        onClick={() => window.open(`${API_BASE}/admin/usuarios/crear`, '_blank')}
        title="Alta manual de un usuario del dashboard para este tenant"
        style={{ background: 'none', border: 'none', cursor: 'pointer',
                 color: 'var(--color-accent)', fontSize: 'var(--text-sm)',
                 fontFamily: 'var(--font-ui)', padding: 0 }}
      >
        Crear usuario ↗
      </button>

      {/* La tabla, a la vista (Manuel, 29-08): un contador sin nombres obligaba
          a creerse el «2» sin poder verificarlo — que es justo lo que el rediseño
          vino a quitar. Una fila por usuario del tenant, con su recepción. */}
      {(usuarios?.lista || []).length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse',
                        fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-text-3)',
                         fontSize: 'var(--text-xs)' }}>
              <th style={{ padding: '2px 8px 2px 0', fontWeight: 500 }}>Nombre</th>
              <th style={{ padding: '2px 8px', fontWeight: 500 }}>Email</th>
              <th style={{ padding: '2px 8px', fontWeight: 500 }}>Estado</th>
              <th style={{ padding: '2px 8px', fontWeight: 500 }}>Resumen semanal</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.lista.map(u => (
              <tr key={u.email} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '3px 8px 3px 0' }}>
                  {u.nombre}
                  {u.es_propietario && (
                    <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-micro)',
                                   fontFamily: 'var(--font-mono)', color: 'var(--color-text-3)' }}>
                      propietario
                    </span>
                  )}
                </td>
                <td style={{ padding: '3px 8px', fontFamily: 'var(--font-mono)' }}>{u.email}</td>
                <td style={{ padding: '3px 8px',
                             color: u.activo ? 'var(--color-ok)' : 'var(--color-text-3)' }}>
                  {u.activo ? 'activo' : 'inactivo'}
                </td>
                <td style={{ padding: '3px 8px',
                             color: u.activo && u.recibe_resumen
                               ? 'var(--color-text-2)' : 'var(--color-text-3)' }}>
                  {u.activo && u.recibe_resumen ? 'lo recibe' : 'no lo recibe'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}


function InfoTab({ cliente }) {
  // Compactado del 28-08 (Manuel): los tres cuadros en UNA fila y la URL en
  // una línea fina — la pestaña Contrato tiene que caber sin scroll.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
      {/* Nombre, correo, ID y convenio los dice ya la cabecera de la ficha:
          repetirlos aquí era el precio de apilar las pestañas viejas. Y la
          «Familia CM» salía en crudo (`alimentos2`) donde toca el nombre del
          convenio — el slug es un identificador técnico de la tienda. */}
      {[
        ['Convenio', cliente.convenio || cliente.familia_cm],
        ['Tier', <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>{cliente.tier}</Badge>],
        ['Estado', <Badge color={cliente.activo ? 'green' : 'gray'} dot>{cliente.activo ? 'Activo' : 'Inactivo'}</Badge>],
      ].map(([label, value]) => (
        <div key={label} style={{
          padding: '9px 12px', background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        }}>
          <div style={{ fontSize: 'var(--text-micro)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '3px' }}>
            {label}
          </div>
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text)' }}>{value}</div>
        </div>
      ))}
      <div style={{
        gridColumn: '1/-1', padding: '7px 12px', background: 'var(--color-surface-2)',
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0,
      }}>
        <span style={{ fontSize: 'var(--text-micro)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', flexShrink: 0 }}>URL BASE</span>
        <span style={{
          fontSize: 'var(--text-sm)', color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={cliente.url_base}>
          {cliente.url_base}
        </span>
      </div>
    </div>
  )
}

function EmpresasTab({ empresas, clienteId }) {
  const updateMutation = useUpdateEmpresa(clienteId)
  const [editando, setEditando] = useState(null)   // empresa en edición | null
  const [form, setForm] = useState({ rut: '', nombre: '', seller_code: '' })
  const [verificando, setVerificando] = useState(false)
  const [hallazgo, setHallazgo] = useState(null)   // {empresa, seller_code} | {error}
  const [saveError, setSaveError] = useState('')
  const [savedId, setSavedId] = useState(null)

  function abrirEdicion(emp) {
    setEditando(emp)
    setForm({ rut: emp.rut ?? '', nombre: emp.nombre ?? '', seller_code: emp.seller_code ?? '' })
    setHallazgo(null)
    setSaveError('')
  }

  function cerrarEdicion() {
    setEditando(null)
    setHallazgo(null)
    setSaveError('')
  }

  // Verifica el RUT contra Mercado Público y autocompleta nombre + seller_code
  // (mismo endpoint que usa el wizard de onboarding).
  async function verificarRut() {
    const rut = form.rut.trim()
    if (!rut) { setHallazgo({ error: 'Escribe el RUT primero.' }); return }
    setVerificando(true); setHallazgo(null)
    try {
      const { data } = await api.get('/api/diagnostico/confirmar', { params: { rut } })
      if (data?.encontrada && data?.seller_code) {
        setForm(f => ({
          ...f,
          rut:         data.rut || f.rut,
          nombre:      data.empresa || f.nombre,
          seller_code: String(data.seller_code),
        }))
        setHallazgo({ empresa: data.empresa, seller_code: data.seller_code })
      } else {
        setHallazgo({ error: 'No encontramos esa empresa como proveedor del Estado. Revisa el RUT.' })
      }
    } catch (err) {
      setHallazgo({ error: 'No se pudo verificar el RUT ahora. Inténtalo de nuevo.' })
    } finally { setVerificando(false) }
  }

  async function guardar() {
    setSaveError('')
    const data = {
      rut:         form.rut.trim(),
      nombre:      form.nombre.trim(),
      seller_code: form.seller_code.trim(),
    }
    if (!data.rut || !data.nombre || !data.seller_code) {
      setSaveError('RUT, nombre y seller code son obligatorios.')
      return
    }
    try {
      const empId = editando.id
      await updateMutation.mutateAsync({ empId, data })
      cerrarEdicion()
      setSavedId(empId)
      setTimeout(() => setSavedId(null), 2500)
    } catch (err) {
      setSaveError(err?.response?.data?.detail || 'No se pudo guardar. Inténtalo de nuevo.')
    }
  }

  const COLS = [
    { key: 'rut', label: 'RUT', mono: true },
    { key: 'nombre', label: 'Nombre' },
    { key: 'seller_code', label: 'Seller Code', mono: true },
    {
      key: 'acciones', label: '', width: '90px', align: 'right',
      render: (_, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {savedId === row.id && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-ok)', fontFamily: 'var(--font-mono)' }}>✓</span>
          )}
          <Button
            size="sm" variant="secondary"
            icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>}
            onClick={() => abrirEdicion(row)}
          >
            Editar
          </Button>
        </span>
      ),
    },
  ]

  return (
    <div>
      <Table columns={COLS} data={empresas} emptyMessage="Sin empresas propias" />

      {editando && (
        <div style={{
          marginTop: 'var(--space-4)', padding: 'var(--space-4)',
          background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '14px' }}>
            EDITAR EMPRESA · #{editando.id}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)', alignItems: 'end' }}>
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
              <Input
                label="RUT"
                value={form.rut}
                onChange={e => setForm(f => ({ ...f, rut: e.target.value }))}
                placeholder="76.000.000-0"
                style={{ flex: 1 }}
              />
              <Button size="md" variant="secondary" onClick={verificarRut} loading={verificando}>
                Verificar
              </Button>
            </div>
            <Input
              label="NOMBRE"
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              placeholder="Razón social"
            />
            <Input
              label="SELLER CODE"
              value={form.seller_code}
              onChange={e => setForm(f => ({ ...f, seller_code: e.target.value }))}
              placeholder="Código proveedor MP"
            />
          </div>

          {hallazgo?.empresa && (
            <div style={{ marginTop: '10px', fontSize: 'var(--text-sm)', color: 'var(--color-ok)' }}>
              ✓ {hallazgo.empresa} · código {hallazgo.seller_code}
            </div>
          )}
          {hallazgo?.error && (
            <div style={{ marginTop: '10px', fontSize: 'var(--text-sm)', color: 'var(--color-error)' }}>
              {hallazgo.error}
            </div>
          )}
          {saveError && (
            <div style={{ marginTop: '10px', fontSize: 'var(--text-sm)', color: 'var(--color-error)' }}>
              {saveError}
            </div>
          )}

          <div style={{ marginTop: '14px', display: 'flex', gap: 'var(--space-2)' }}>
            <Button onClick={guardar} loading={updateMutation.isPending}>
              Guardar cambios
            </Button>
            <Button variant="ghost" onClick={cerrarEdicion} disabled={updateMutation.isPending}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function CompetidoresTab({ competidores, clienteId }) {
  const updateMutation = useUpdateCompetidor(clienteId)

  const COLS = [
    { key: 'rut', label: 'RUT', mono: true },
    { key: 'nombre', label: 'Nombre' },
    { key: 'seller_code', label: 'Seller Code', mono: true },
    {
      key: 'activo', label: 'Activo', width: '70px', align: 'center',
      render: (v, row) => (
        <Toggle
          checked={v}
          onChange={(val) => updateMutation.mutate({ compId: row.id, data: { activo: val } })}
        />
      ),
    },
  ]
  return <Table columns={COLS} data={competidores} emptyMessage="Sin competidores configurados" />
}

function SchedulerTab({ scheduler, clienteId }) {
  const updateMutation = useUpdateScheduler(clienteId)
  const [local, setLocal] = useState({
    activo_precios: scheduler?.activo_precios ?? false,
    freq_precios: scheduler?.freq_precios ?? '48h',
    activo_ocs: scheduler?.activo_ocs ?? false,
    freq_ocs: scheduler?.freq_ocs ?? 'semanal',
  })
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  async function handleSave() {
    setError(null)
    try {
      await updateMutation.mutateAsync(local)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar la configuración')
    }
  }

  function Row({ label, activeKey, freqKey }) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 0', borderBottom: '1px solid var(--color-border)',
      }}>
        <div>
          <div style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--color-text)' }}>{label}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginTop: 'var(--space-05)' }}>
            {local[activeKey] ? `Activo · ${local[freqKey]}` : 'Inactivo'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Select
            value={local[freqKey]}
            onChange={e => setLocal(l => ({ ...l, [freqKey]: e.target.value }))}
            options={FREQ_OPTS}
            style={{ width: '130px' }}
          />
          <Toggle
            checked={local[activeKey]}
            onChange={val => setLocal(l => ({ ...l, [activeKey]: val }))}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <Row label="Scraping de precios" activeKey="activo_precios" freqKey="freq_precios" />
      <Row label="Scraping de OCs" activeKey="activo_ocs" freqKey="freq_ocs" />
      <div style={{ marginTop: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Guardar configuración
        </Button>
        {saved && (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-ok)', fontFamily: 'var(--font-mono)' }}>
            ✓ Guardado
          </span>
        )}
        {error && (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>
            {error}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Uso / Plan (companion admin del indicador de uso) ─────────────────────────

// ⚠️ Era `(n ?? 0).toLocaleString('es-CL')` — la MISMA función, letra por
// letra, que quité de CifrasLanding.jsx en la fase 01 y que allí hacía que
// un dato ausente encendiera un veredicto en rojo. Aquí sobrevivió porque
// la guarda de entonces solo miró aquel fichero.
const _fmt = (n) => (n === null || n === undefined ? '—' : n.toLocaleString('es-CL'))

// La barra de uso de un tope del plan.
//
// `tramos` (02-09) la parte por dentro sin añadir una segunda barra. Nace de la
// de familias, donde el dibujo decía «11, sanas» y debajo hacían falta TRES
// frases para desmentirlo: que solo vigila 4, que 2 de las 11 no son familias
// de verdad y que el tope del plan son 4. Ahora eso se ve, y las tres frases
// sobran. Cada tramo se lleva su parte del relleno en proporción a `n`, así que
// el ancho total sigue significando lo mismo que antes: uso frente al tope.
function UsoBar({ label, m, extra, tramos, pista }) {
  // m = {n, lim, over, mon?}. lim=0 → ilimitado.
  if (!m) return null
  const ilim = m.lim === 0
  const pct = ilim ? 100 : Math.min(100, Math.round((m.n / Math.max(1, m.lim)) * 100))
  const col = m.over ? 'var(--color-error)' : (ilim ? 'var(--color-ok)' : 'var(--color-accent)')
  const partes = (tramos || []).filter(t => t && Number(t.valor) > 0)
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', marginBottom: '5px' }}>
        <span style={{ color: 'var(--color-text)' }}>{label}</span>
        <span style={{ fontWeight: 600, color: col }}>
          {ilim ? `${_fmt(m.n)} · ilimitado` : `${_fmt(m.n)} / ${_fmt(m.lim)}`}{m.over ? ' ⚠' : ''}
          {pista && <> <Pista contenido={pista} etiqueta={label} /></>}
        </span>
      </div>
      <div style={{ height: '6px', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}>
        {partes.length ? (
          <div data-barra="uso" style={{ height: '100%', width: `${pct}%`, display: 'flex', gap: '1px' }}>
            {partes.map(t => (
              <div key={t.clave} data-tramo={t.clave}
                   style={{ width: `${(Number(t.valor) / Math.max(1, m.n)) * 100}%`,
                            background: t.color || col }} />
            ))}
          </div>
        ) : (
          <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 'var(--radius-pill)' }} />
        )}
      </div>
      {partes.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1) var(--space-3)',
                      marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-3)', lineHeight: 1.6 }}>
          {partes.map(t => (
            <span key={t.clave} data-leyenda={t.clave}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <span aria-hidden="true" style={{ width: '9px', height: '9px', borderRadius: 'var(--radius-pill)',
                                                flexShrink: 0, background: t.color || col }} />
              <span><strong style={{ color: 'var(--color-text-2)' }}>{_fmt(t.valor)}</strong> {t.etiqueta}</span>
              {t.pista && <Pista contenido={t.pista} etiqueta={t.etiqueta} />}
              {t.extra}
            </span>
          ))}
        </div>
      )}
      {extra && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginTop: 'var(--space-1)' }}>{extra}</div>}
    </div>
  )
}

// De qué está hecha la cifra de familias. Devuelve `null` cuando no hay nada
// que descomponer, y entonces la barra se pinta entera como siempre.
//
// ⚠️ `sin_taxonomia === null` significa «no se ha medido», y ahí NO se inventa
// un tramo de cero (§3): se enseñan solo los dos tramos que sí se conocen. El
// snapshot de un tenant que no ha vuelto a pasar por el pipeline es justo ese
// caso, y decir «0 sin clasificar» sería afirmar algo que nadie ha comprobado.
export function tramosFamilias(f) {
  if (!f || f.mon == null || f.n == null) return null
  // ⚠️ `sin_taxonomia === null` es «no se ha medido», y NO se convierte en cero.
  // Aquí eso no se ve en el tramo —que no medir y que no haya ninguna dan los
  // dos un tramo vacío— pero sí se ve en el resto: si no sabemos cuántas de
  // esas familias son basura de taxonomía, no podemos llamarlas a todas
  // «detectadas sin vigilar» sin más. Se dice que no se sabe.
  const medida = f.sin_taxonomia != null
  const sueltas = medida ? f.sin_taxonomia : 0
  const resto = Math.max(0, f.n - f.mon - sueltas)
  if (!f.mon && !resto && !sueltas) return null
  return [
    { clave: 'vigiladas', valor: f.mon, etiqueta: 'vigiladas',
      color: 'var(--color-accent)' },
    { clave: 'sin-vigilar', valor: resto, etiqueta: 'detectadas sin vigilar',
      color: 'var(--color-border-2)',
      pista: medida
        ? 'Las tiene en catálogo y el plan no le alcanza para vigilarlas, o están '
          + 'apagadas a mano. Es la cobertura que le falta.'
        : 'Cobertura que le falta… o no. En este tenant NO se ha medido cuántas de '
          + 'estas son categorías que ninguna taxonomía sabe agrupar, así que '
          + 'algunas pueden no ser familias de verdad. Se mide en el próximo run '
          + 'de su pipeline.',
      extra: medida ? null : (
        <span data-sin-medir="taxonomia" style={{ color: 'var(--color-warn)' }}>
          · reparto sin medir
        </span>
      ) },
    // ⚠️ «categorías, no familias» y no «sin clasificar» a secas. Dentro de una
    // barra rotulada «Familias», un «2 sin clasificar» se lee como «2 familias
    // pendientes de clasificar», que es JUSTO el malentendido contra el que
    // avisó Manuel el 31-08: no son familias que falten por crear, son
    // categorías del portal que hay que plegar en las que ya existen.
    { clave: 'sin-clasificar', valor: sueltas, etiqueta: 'categorías, no familias',
      color: 'var(--color-warn)',
      // Sólido, no rayado, y la diferencia importa: esto SÍ está medido —
      // sabemos cuántas son—, lo que pasa es que no son familias de verdad.
      // El rayado de la casa significa «no sabemos dónde cae esto», y usarlo
      // aquí mentiría.
      extra: <ListaSinClasificar f={f} /> },
  ]
}


function UsoTab({ clienteId }) {
  const { data, isLoading, error } = useUso(clienteId)
  const muted = { color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', padding: '8px 0' }
  if (isLoading) return <div style={muted}>Cargando uso…</div>
  if (error) return <div style={{ ...muted, color: 'var(--color-error)' }}>No se pudo cargar el uso.</div>

  const uso = data?.uso || {}
  if (!uso.tier) return (
    <div style={muted}>
      Aún no hay snapshot de uso. Se genera en el primer run del pipeline del cliente
      (regenerar dashboard).
    </div>
  )

  const f = uso.familias, p = uso.productos
  const anyOver = ['familias', 'productos', 'competidores', 'empresas'].some(k => uso[k]?.over)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-base)', fontWeight: 600 }}>
          Plan <Badge color={TIER_COLOR[uso.tier] || 'gray'}>{uso.tier}</Badge>
        </div>
        {anyOver && uso.siguiente && (
          <Badge color="yellow">Oportunidad de upsell → {uso.siguiente}</Badge>
        )}
      </div>

      <UsoBar label="Familias monitorizadas" m={f} tramos={tramosFamilias(f)}
        pista={<>
          A la derecha, <b>cuántas familias se le han detectado</b> frente a
          <b> cuántas permite su plan</b>. Cuando la primera es mayor, sale ⚠:
          es la señal de que el plan se le ha quedado corto, no un fallo.
        </>}
        extra={tramosFamilias(f) ? null
          : (f && f.mon != null && f.mon !== f.n ? `Monitorea ${_fmt(f.mon)} de ${_fmt(f.n)} familias detectadas` : null)} />
      <FacturaEnFamilias f={f} />
      <UsoBar label="Productos propios" m={p}
        extra={p && p.mon != null && p.mon !== p.n ? `Monitorea ${_fmt(p.mon)} de ${_fmt(p.n)} productos del catálogo` : null} />
      <UsoBar label="Competidores" m={uso.competidores} />
      <UsoBar label="Empresas propias" m={uso.empresas} />

      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginTop: '10px', lineHeight: 1.5 }}>
        <span style={{ color: 'var(--color-error)' }}>⚠</span> = por encima del tope del plan (señal de upsell).
        {data?.actualizado_en && <> · Actualizado: {fechaHoraCL(data.actualizado_en)}</>}
      </div>
    </div>
  )
}

// Dónde ha facturado el tenant, familia a familia. Pedido por Manuel el 01-09
// para poder discutir el tope de familias por tier con cifras: «ningún proveedor
// con 19 familias va a contratar un servicio que solo le muestra 4».
//
// El 02-09 esto era una frase de 81 caracteres más una fila de cuatro nombres
// con sus importes. En esa forma se perdía lo único que había que ver: que
// **«sin clasificar» es el trozo MÁS GRANDE**, más que abarrotes. Iba al final
// de la frase, en gris, del mismo tamaño que todo lo demás. Ordenado por dinero
// y dibujado, es la primera línea y ya no hay que leer nada para verlo.
//
// ⚠️ La lista NO se corta en silencio. `fact_top` trae ocho familias y puede
// haber más; lo que no cabe aparece como «resto de familias» con su importe, de
// modo que las barras siempre suman el total. Enseñar ocho barras que no llegan
// al total sería el mismo defecto que motivó BarraTramos.
// Qué significa cada tramo que NO es una familia, y —lo que importa— qué se
// hace con él. Los tres se veían como uno solo hasta el 02-09 y sus remedios
// son distintos: taxonomía, catálogo, y nada (el dato no viene de nosotros).
const MOTIVO_TEXTO = {
  sin_clasificar:
    'Códigos que SÍ están en el catálogo vigilado y no caen en ninguna familia: '
    + 'se miraron y no se supieron agrupar. Se arregla añadiendo esas categorías '
    + 'a la taxonomía de la tienda.',
  fuera_del_catalogo:
    'Códigos que el catálogo vigilado NO cubre: no es que no se sepan agrupar, '
    + 'es que no se han mirado. La taxonomía no tiene nada que ver — se amplía '
    + 'el catálogo del tenant.',
  sin_codigo:
    'Líneas de orden de compra que no traen código de producto. No se pueden '
    + 'clasificar desde aquí: el dato falta en origen.',
}

// ⚠️ 03-09. Manuel, mirando el panel de solofresco: «se ha creado una familia
// que no es tal (trutro-corto-pavo); eso es simplemente un producto que debería
// estar en carnes-pescados-y-cecinas». No era una familia inventada: era una
// SUBfamilia real del portal que el rollup de la tienda no traduce, dibujada en
// el mismo ranking que las familias de verdad. Dos niveles compitiendo en una
// sola lista sesgan la lectura entera, así que aquí van agrupadas en UNA fila
// —con su lista dentro, que es la cola de trabajo— en vez de intercaladas.
// ⚠️ 04-09: esta fila decía «N subfamilias sin asignar» y el chip de arriba
// «N categorías, no familias», con N distintos en la misma pantalla (Manuel lo
// vio en demo: 2 aquí, 1 allí). No era un fallo de cuentas: el chip cuenta el
// catálogo PROPIO del tenant y esto contaba el CAT entero. Dos definiciones del
// mismo hecho — §4. Ahora esta fila habla SOLO de dinero, que es su universo, y
// la cola de trabajo completa vive donde ya vivía: el chip y su lista.
const SUBFAM_TEXTO =
  'Categorías reales del portal que todavía no están traducidas a su familia y '
  + 'que SÍ han facturado. No son familias: son un nivel por debajo, y mientras '
  + 'no se traduzcan compiten en la gráfica con las familias de verdad. La lista '
  + 'completa de categorías por traducir —facturen o no— está arriba, en '
  + '«categorías, no familias».'

function _ejemplosDe(f, motivo) {
  return (f.fact_sin_clas_top || []).filter(h => h.motivo === motivo)
}

function FacturaEnFamilias({ f }) {
  if (!f || f.fact == null) return null      // no medido ≠ cero
  const sin = f.fact_sin_clas || 0
  // ⚠️ 02-09: esta fila decía «sin clasificar» y sumaba DOS cosas opuestas. Con
  // el catálogo de `solofresco` encogido, el 98 % del dinero cayó ahí y la
  // pantalla dijo que sus productos «no se sabían clasificar» cuando la
  // taxonomía estaba perfecta y lo que pasaba es que no se habían MIRADO.
  // Separadas: la de arriba se arregla con taxonomía, la de abajo con catálogo.
  const fuera = f.fact_fuera_cat || 0
  const sinCod = f.fact_sin_codigo || 0
  const subfam = f.fact_subfam_monto || 0
  const clasificado = f.fact_monto || 0
  const total = clasificado + sin + fuera + sinCod + subfam
  if (!total) return null

  const top = (f.fact_top || []).map(([nombre, monto]) => ({
    clave: nombre, etiqueta: nombre, monto: Number(monto) || 0,
  }))
  const resto = clasificado - top.reduce((a, t) => a + t.monto, 0)

  const filas = [...top]
  if (sin > 0) filas.push({ clave: '__sin_clasificar__', etiqueta: 'sin clasificar',
                            monto: sin, aviso: true, motivo: 'sin_clasificar' })
  if (fuera > 0) filas.push({ clave: '__fuera_catalogo__',
                              etiqueta: 'fuera del catálogo medido',
                              monto: fuera, aviso: true, motivo: 'fuera_del_catalogo' })
  if (sinCod > 0) filas.push({ clave: '__sin_codigo__',
                               etiqueta: 'líneas de OC sin código',
                               monto: sinCod, aviso: true, motivo: 'sin_codigo' })
  // Una sola fila, aunque dentro haya 20 subfamilias: el detalle está en su
  // «ver qué es». Intercalarlas era el sesgo, no el volumen.
  // Condicionada al DINERO, no al recuento: en una gráfica de importes una fila
  // de $0 no dice nada y se confundía con el chip de arriba (§4, ver SUBFAM_TEXTO).
  if (subfam > 0) filas.push({
    clave: '__subfamilias__',
    etiqueta: `${_fmt(f.fact_subfam_n)} subfamilia${f.fact_subfam_n === 1 ? '' : 's'} sin asignar`,
    monto: subfam, aviso: true, subfam: true,
  })
  if (resto > 0) filas.push({ clave: '__resto__', etiqueta: 'resto de familias',
                              monto: resto })
  filas.sort((a, b) => b.monto - a.monto)

  const mayor = filas[0]?.monto || 1

  return (
    <div style={{ marginTop: 'calc(var(--space-2) * -1)', marginBottom: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)',
                    fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                    marginBottom: 'var(--space-1)' }}>
        <span style={{ color: 'var(--color-text-2)' }}>
          <strong>{_fmt(f.fact)}</strong> familias · {clp(total)}
        </span>
        <Pista etiqueta="dónde ha facturado" contenido={<>
          <div style={{ marginBottom: 'var(--space-2)' }}>
            Sale del <b>historial de órdenes de compra</b>, no del catálogo de hoy.
            Son dos universos distintos: una familia en la que vendió y de la que
            ya retiró los productos cuenta aquí y no arriba.
          </div>
          {f.n != null && f.fact !== f.n && (
            <div>
              {f.fact > f.n
                ? `Por eso ${_fmt(f.fact - f.n)} de estas familias no están en las ${_fmt(f.n)} de su catálogo de hoy: facturó ahí y ya no ofrece nada.`
                : `De las ${_fmt(f.n)} que tiene en catálogo hoy, ${_fmt(f.n - f.fact)} no le han facturado nunca.`}
            </div>
          )}
        </>}>Dónde ha facturado</Pista>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 190px) minmax(0, 1fr) auto',
                    gap: 'var(--space-1) var(--space-2)', alignItems: 'center',
                    fontSize: 'var(--text-xs)' }}>
        {filas.map(fila => (
          <Fragment key={fila.clave}>
            <span data-familia={fila.clave} style={{
              color: fila.aviso ? 'var(--color-warn)' : 'var(--color-text-2)',
              overflowWrap: 'anywhere',
            }}>
              {fila.etiqueta}
              {/* Qué HAY dentro de lo no clasificado (H1, 02-09). Sin esto el
                  porcentaje solo produce inquietud: no se puede actuar sobre un
                  número. Con el código y el nombre delante casi siempre se ve al
                  vuelo si falta una familia, si es otro convenio o si son líneas
                  sin código. Es una HERRAMIENTA, no una explicación, y por eso
                  conserva su botón — igual que la lista de categorías. */}
              {fila.subfam && (f.fact_subfam || []).length > 0 && (
                <> <Pista ancho={460} etiqueta="qué subfamilias faltan" contenido={
                  <div>
                    <div style={{ marginBottom: 'var(--space-2)' }}>{SUBFAM_TEXTO}</div>
                    <div style={{ fontFamily: 'var(--font-mono)' }}>
                      {(f.fact_subfam || []).slice(0, 20).map(s => (
                        <div key={s.slug}>
                          {clp(s.monto)} · {s.slug}
                          {s.productos > 0 ? ` · ${_fmt(s.productos)} prod.` : ''}
                        </div>
                      ))}
                    </div>
                    {(f.fact_subfam || []).length > 20 && (
                      <div style={{ marginTop: 'var(--space-2)' }}>
                        …y {_fmt(f.fact_subfam.length - 20)} más.
                      </div>
                    )}
                  </div>}>
                  <span style={{ color: 'var(--color-accent)' }}>ver cuáles</span>
                </Pista></>
              )}
              {fila.motivo && _ejemplosDe(f, fila.motivo).length > 0 && (
                <> <Pista ancho={420} etiqueta={`qué hay en «${fila.etiqueta}»`} contenido={
                  <div>
                    <div style={{ marginBottom: 'var(--space-2)' }}>
                      {MOTIVO_TEXTO[fila.motivo]}
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)' }}>
                      {_ejemplosDe(f, fila.motivo).map(h => (
                        <div key={h.codigo}>
                          {clp(h.monto)} · {h.codigo}{h.nombre ? ` · ${h.nombre}` : ''}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 'var(--space-2)' }}>
                      En total <b>{_fmt(f.fact_sin_clas_n)} códigos</b> repartidos
                      entre los tramos de aviso; aquí se muestran los que más pesan.
                    </div>
                  </div>}>
                  <span style={{ color: 'var(--color-accent)' }}>ver qué es</span>
                </Pista></>
              )}
            </span>
            <span aria-hidden="true" style={{ height: '9px', background: 'var(--color-surface-2)',
                                              borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}>
              <span style={{ display: 'block', height: '100%',
                             width: `${Math.max(2, (fila.monto / mayor) * 100)}%`,
                             background: fila.aviso ? 'var(--color-warn)' : 'var(--color-accent)',
                             borderRadius: 'var(--radius-pill)' }} />
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums',
                           color: 'var(--color-text-3)', whiteSpace: 'nowrap' }}>
              {clp(fila.monto)}{' '}
              <span style={{ color: fila.aviso ? 'var(--color-warn)' : 'var(--color-text-3)' }}>
                {_pct(fila.monto, total)}%
              </span>
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  )
}


// La lista de categorías que ninguna taxonomía sabe agrupar, dentro de la pista
// del tramo que ya las cuenta en la barra.
//
// Antes era un párrafo de aviso de 108 caracteres bajo la barra. El aviso ya no
// hace falta —el tramo naranja de la barra lo dice sin escribirlo—, pero la
// LISTA sí: es una herramienta, no una explicación. Lo que hay que hacer con
// ella es copiarla entera y pegarla en el fichero de taxonomía.
//
// ⚠️ Esto NO es una lista de familias que falten por crear. Es al revés: son las
// categorías que hay que PLEGAR dentro de las familias que ya existen. En
// `solofresco` son 92 categorías que van a parar a 6 familias, no 92 familias.
//
// En un cuadro de texto seleccionable y no en una lista de <li>, porque si el
// botón de copiar falla (permisos del navegador) un Ctrl+A dentro del cuadro
// sigue funcionando.
function ListaSinClasificar({ f }) {
  const [copiado, setCopiado] = useState(false)
  const lista = f?.sueltas || []
  if (!f || !f.sin_taxonomia || lista.length === 0) return null

  const texto = lista.join('\n')
  const recortada = f.sin_taxonomia > lista.length

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      setCopiado(false)   // sin permiso de portapapeles: queda el cuadro de texto
    }
  }

  return (
    <Pista ancho={380} etiqueta="categorías sin clasificar" contenido={
      <div>
        <div style={{ marginBottom: 'var(--space-2)' }}>
          Categorías del portal que ninguna taxonomía sabe agrupar, así que cada
          una se cuenta como si fuera una familia entera. <b>No son familias que
          falten por crear</b>: son categorías que hay que plegar dentro de las
          que ya existen, en{' '}
          <span style={{ fontFamily: 'var(--font-mono)' }}>
            data/referencia/subfamilia_&lt;tienda&gt;.json
          </span>.
        </div>
        <textarea readOnly value={texto} rows={8}
          style={{ width: '100%', fontFamily: 'var(--font-mono)',
                   fontSize: 'var(--text-xs)', color: 'var(--color-text-2)',
                   background: 'var(--color-surface-2)',
                   border: '1px solid var(--color-border)',
                   borderRadius: 'var(--radius-sm)', padding: 'var(--space-2)' }} />
        <div style={{ marginTop: 'var(--space-05)' }}>
          <button type="button" onClick={copiar}
                  style={{ background: 'none', border: 'none', padding: 0,
                           color: 'var(--color-accent)', cursor: 'pointer',
                           fontSize: 'var(--text-xs)', textDecoration: 'underline' }}>
            {copiado ? 'copiado ✓' : 'copiar las ' + _fmt(lista.length)}
          </button>
          {recortada && (
            <span style={{ marginLeft: 'var(--space-2)' }}>
              (se muestran {_fmt(lista.length)} de {_fmt(f.sin_taxonomia)})
            </span>
          )}
        </div>
      </div>
    }>
      <span style={{ color: 'var(--color-accent)' }}>ver lista</span>
    </Pista>
  )
}


function ConcesionSeccion({ cliente, clienteId }) {
  const overrideMut = useUpdateTierOverride(clienteId)
  const hayOverride = !!cliente.tier_override && cliente.tier_efectivo !== cliente.tier


  // ── Concesión de tier ──
  const [tier, setTier] = useState(cliente.tier_override || 'premium')
  const [modo, setModo] = useState('indefinido')   // indefinido | dias | fecha
  const [dias, setDias] = useState(30)
  const [hasta, setHasta] = useState('')
  const [motivo, setMotivo] = useState(cliente.tier_override_motivo || '')
  const [msg, setMsg] = useState(null)             // {ok, text}

  async function conceder() {
    setMsg(null)
    const payload = { tier, motivo: motivo.trim() || null }
    if (modo === 'dias') payload.dias = Number(dias)
    if (modo === 'fecha') {
      if (!hasta) { setMsg({ ok: false, text: 'Elige una fecha de vencimiento.' }); return }
      payload.hasta = hasta
    }
    try {
      await overrideMut.mutateAsync(payload)
      setMsg({ ok: true, text: 'Concesión aplicada.' })
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'No se pudo aplicar.' })
    }
  }

  async function quitar() {
    setMsg(null)
    try {
      await overrideMut.mutateAsync({ tier: null })
      setMsg({ ok: true, text: 'Concesión retirada.' })
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'No se pudo retirar.' })
    }
  }


  // Compactado del 28-08 (Manuel): «Plan actual» pasa a una fila fina y los
  // controles de concesión caben en una línea — Contrato sin scroll.
  const box = {
    padding: 'var(--space-3) var(--space-4)', background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-3)',
  }
  const titulo = {
    fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
    letterSpacing: '0.08em', marginBottom: 'var(--space-2)',
  }

  return (
    <div>
      {/* Resumen paga / disfruta — una sola fila, con el rótulo dentro. */}
      <div style={{ ...box, display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <span style={{ ...titulo, marginBottom: 0 }}>PLAN ACTUAL</span>
        <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-2)' }}>
          Paga <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>{cliente.tier}</Badge>
        </span>
        <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-2)' }}>
          Disfruta <Badge color={TIER_COLOR[cliente.tier_efectivo] || 'gray'}>{cliente.tier_efectivo}</Badge>
        </span>
        {hayOverride && (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
            · concesión {cliente.tier_override_hasta
              ? `hasta ${_fmtFecha(cliente.tier_override_hasta)}`
              : 'indefinida'}
            {cliente.tier_override_motivo ? ` (${cliente.tier_override_motivo})` : ''}
          </span>
        )}
      </div>

      {/* Conceder tier */}
      <div style={box}>
        <div style={titulo}>CONCEDER TIER (SIN TOCAR LO QUE PAGA)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)', alignItems: 'end' }}>
          <Select label="TIER A CONCEDER" value={tier} onChange={e => setTier(e.target.value)} options={TIER_OPTS} />
          <Select label="VIGENCIA" value={modo} onChange={e => setModo(e.target.value)} options={[
            { value: 'indefinido', label: 'Indefinida (hasta nuevo cambio)' },
            { value: 'dias', label: 'Prueba de N días' },
            { value: 'fecha', label: 'Hasta una fecha' },
          ]} />
          {modo === 'dias' && (
            <Input label="DÍAS DE PRUEBA" type="number" value={dias} onChange={e => setDias(e.target.value)} />
          )}
          {modo === 'fecha' && (
            <Input label="VENCE EL" type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
          )}
          <Input label="MOTIVO (OPCIONAL)" value={motivo} onChange={e => setMotivo(e.target.value)}
                 placeholder="prueba comercial, cortesía…" />
        </div>
        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button onClick={conceder} loading={overrideMut.isPending}>Aplicar concesión</Button>
          {hayOverride && (
            <Button variant="ghost" onClick={quitar} loading={overrideMut.isPending}>Quitar concesión</Button>
          )}
          {msg && (
            <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
              color: msg.ok ? 'var(--color-ok)' : 'var(--color-error)' }}>
              {msg.ok ? '✓' : '✗'} {msg.text}
            </span>
          )}
        </div>
      </div>

    </div>
  )
}


/**
 * La zona de riesgo: lo que corta el servicio o el cobro.
 *
 * ⚠️ Estaba dentro de `PlanTab`, al MISMO nivel visual que «Guardar estado» y
 * que la concesión de tier. Cancelar el cobro de un cliente no puede parecerse
 * a guardar un formulario. Aquí va aparte, plegada, con borde rojo, y cada
 * acción dice qué pasa y si se puede deshacer.
 */
function ZonaRiesgo({ cliente, clienteId, onDesactivar, desactivando }) {
  const [abierta, setAbierta] = useState(false)
  const estadoMut = useUpdateEstado(clienteId)
  const cancelarMut = useCancelarSuscripcion(clienteId)
  const [msgSusc, setMsgSusc] = useState(null)

  async function cancelarSusc() {
    if (!window.confirm('¿Cancelar la renovación de esta suscripción? Se detiene el cobro en Flow y el servicio sigue hasta el fin del periodo pagado.')) return
    setMsgSusc(null)
    try {
      await cancelarMut.mutateAsync()
      setMsgSusc({ ok: true, text: 'Suscripción cancelada. Baja programada al fin del periodo.' })
    } catch (e) {
      setMsgSusc({ ok: false, text: e?.response?.data?.detail || 'No se pudo cancelar.' })
    }
  }


  // ── Estado / baja ──
  const [activo, setActivo] = useState(cliente.activo)
  const [programarBaja, setProgramarBaja] = useState(!!cliente.baja_programada_en)
  const [baja, setBaja] = useState(cliente.baja_programada_en ? cliente.baja_programada_en.slice(0, 10) : '')
  const [msgEstado, setMsgEstado] = useState(null)

  // Fecha por defecto de la baja: el fin del periodo ya pagado (Flow) si se conoce;
  // si no, el fin del mes en curso. El admin puede ajustarla.
  const _bajaPorDefecto = () => (cliente.periodo_fin ? cliente.periodo_fin.slice(0, 10) : _finDeMes())

  // Red de seguridad: al activar "Programar baja" el campo NUNCA queda en blanco.
  function toggleProgramarBaja(on) {
    setProgramarBaja(on)
    if (on && !baja) setBaja(_bajaPorDefecto())
    setMsgEstado(null)
  }

  async function guardarEstado() {
    setMsgEstado(null)
    // Seguridad: si se pide programar baja, la fecha es obligatoria (no blanco).
    if (programarBaja && !baja) {
      setMsgEstado({ ok: false, text: 'Elige la fecha de baja o desactiva "Programar baja".' })
      return
    }
    try {
      await estadoMut.mutateAsync({ activo, baja_programada_en: programarBaja ? baja : null })
      setMsgEstado({ ok: true, text: 'Estado guardado.' })
    } catch (e) {
      setMsgEstado({ ok: false, text: e?.response?.data?.detail || 'No se pudo guardar.' })
    }
  }


  const box = {
    padding: 'var(--space-4)', background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)',
  }
  const titulo = {
    fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
    letterSpacing: '0.08em', marginBottom: 'var(--space-3)',
  }

  if (!abierta) {
    return (
      <div
        onClick={() => setAbierta(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
          cursor: 'pointer', padding: 'var(--space-2) 0',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
             stroke="var(--color-text-3)" strokeWidth="2.4"><polyline points="9 18 15 12 9 6" /></svg>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
          letterSpacing: '0.13em', textTransform: 'uppercase',
          color: 'var(--color-text-3)',
        }}>
          Zona de riesgo
        </span>
      </div>
    )
  }

  return (
    <div>
      <div
        onClick={() => setAbierta(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
          cursor: 'pointer', padding: 'var(--space-2) 0',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
             stroke="var(--color-error)" strokeWidth="2.4"><polyline points="6 9 12 15 18 9" /></svg>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
          letterSpacing: '0.13em', textTransform: 'uppercase',
          color: 'var(--color-error)',
        }}>
          Zona de riesgo
        </span>
      </div>

      <div style={{
        border: '1px solid var(--color-error-line)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4) var(--space-5)',
        marginTop: 'var(--space-3)',
      }}>
      {/* Estado / baja */}
      <div style={box}>
        <div style={titulo}>ESTADO DEL SERVICIO</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 'var(--space-3)' }}>
          <div>
            <div style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--color-text)' }}>Cliente activo</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginTop: 'var(--space-05)' }}>
              {activo ? 'Recibe scraping y accede a su dashboard' : 'Servicio detenido (dashboard congelado sigue accesible)'}
            </div>
          </div>
          <Toggle checked={activo} onChange={setActivo} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}>
          <div>
            <div style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--color-text)' }}>Programar baja</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginTop: 'var(--space-05)' }}>
              {programarBaja ? 'El servicio se desactivará solo en la fecha indicada' : 'Sin baja programada — el servicio continúa indefinidamente'}
            </div>
          </div>
          <Toggle checked={programarBaja} onChange={toggleProgramarBaja} />
        </div>
        {programarBaja && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', alignItems: 'end', paddingTop: 'var(--space-3)' }}>
            <Input label="FECHA DE BAJA (VENCIMIENTO)" type="date" value={baja}
                   onChange={e => setBaja(e.target.value)}
                   error={!baja ? 'Obligatoria' : ''} />
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', paddingBottom: '9px' }}>
              Por defecto, el fin del mes en curso (p. ej. al agotarse el periodo ya pagado). Ajústala si procede.
            </div>
          </div>
        )}
        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button onClick={guardarEstado} loading={estadoMut.isPending}>Guardar estado</Button>
          {msgEstado && (
            <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
              color: msgEstado.ok ? 'var(--color-ok)' : 'var(--color-error)' }}>
              {msgEstado.ok ? '✓' : '✗'} {msgEstado.text}
            </span>
          )}
        </div>
      </div>

      {/* Suscripción (Flow) */}
      <div style={box}>
        <div style={titulo}>SUSCRIPCIÓN (FLOW)</div>
        {cliente.renovar_automaticamente == null ? (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
            Este cliente no tiene una suscripción de pago vinculada (alta manual o sin Flow).
          </div>
        ) : cliente.renovar_automaticamente ? (
          <>
            <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
              Se renueva automáticamente{cliente.periodo_fin ? ` · próximo cargo hacia ${_fmtFecha(cliente.periodo_fin)}` : ''}.
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginBottom: 'var(--space-3)' }}>
              Cancelar detiene el cobro en Flow; el servicio sigue hasta el fin del periodo ya pagado.
            </div>
            <Button variant="ghost" onClick={cancelarSusc} loading={cancelarMut.isPending}
                    style={{ color: 'var(--color-error)' }}>Cancelar suscripción</Button>
          </>
        ) : (
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-2)' }}>
            ✓ Cancelada — el servicio sigue activo hasta {_fmtFecha(cliente.baja_programada_en || cliente.periodo_fin) || 'el fin del periodo'}.
          </div>
        )}
        {msgSusc && (
          <div style={{ marginTop: '10px', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
            color: msgSusc.ok ? 'var(--color-ok)' : 'var(--color-error)' }}>
            {msgSusc.ok ? '✓' : '✗'} {msgSusc.text}
          </div>
        )}
      </div>

        <div style={{
          borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-4)',
          marginTop: 'var(--space-2)',
        }}>
          <div style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--weight-bold)' }}>
            Desactivar ahora
          </div>
          <div style={{
            fontSize: 'var(--text-base)', color: 'var(--color-text-2)',
            margin: 'var(--space-1) 0 var(--space-3)', lineHeight: 1.5,
          }}>
            Detiene su scheduler y lo saca del listado en el acto. Sus datos se
            conservan y su dashboard congelado sigue accesible.
          </div>
          <Button variant="danger" onClick={onDesactivar} loading={desactivando}>
            Desactivar cliente
          </Button>
        </div>
      </div>
    </div>
  )
}


/** Un bloque de la ficha: rótulo mono y su contenido. Sin tarjeta: filetes. */
function Seccion({ rotulo, children, primera = false }) {
  return (
    <div style={{
      marginTop: primera ? 0 : 'var(--space-7)',
      paddingTop: primera ? 0 : 'var(--space-5)',
      borderTop: primera ? 'none' : '1px solid var(--color-border-2)',
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.13em', textTransform: 'uppercase',
        color: 'var(--color-text-3)', marginBottom: 'var(--space-4)',
      }}>
        {rotulo}
      </div>
      {children}
    </div>
  )
}

/** Rótulo menor dentro de una sección. Dos tablas seguidas sin decir cuál es
    cuál obligan a deducirlo por las columnas. */
function SubRotulo({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
      color: 'var(--color-text-2)', marginBottom: 'var(--space-2)',
    }}>
      {children}
    </div>
  )
}

function CampoFicha({ etiqueta, valor, pie, color = 'var(--color-text)' }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.11em', textTransform: 'uppercase',
        color: 'var(--color-text-3)',
      }}>
        {etiqueta}
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-md)',
        color, marginTop: 'var(--space-1)',
      }}>
        {valor}
      </div>
      {pie && (
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
          color: 'var(--color-text-3)', marginTop: 'var(--space-05)',
        }}>
          {pie}
        </div>
      )}
    </div>
  )
}

const _COLOR_SEMAFORO = {
  verde: 'var(--color-ok)',
  amarillo: 'var(--color-warn)',
  rojo: 'var(--color-error)',
  gris: 'var(--color-text-3)',
}

/** El SLA como semáforo de verdad (Manuel, 28-08): tres luces y la encendida
    dice el estado. La identidad nunca viaja solo en el color — el título va
    al lado, del color de la luz. Gris (inactivo) = las tres apagadas. */
function SemaforoSLA({ semaforo, titulo }) {
  const luces = ['rojo', 'amarillo', 'verde']
  const encendida = { rojo: _COLOR_SEMAFORO.rojo, amarillo: _COLOR_SEMAFORO.amarillo, verde: _COLOR_SEMAFORO.verde }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
         role="img" aria-label={`SLA: ${titulo}`}>
      <span style={{
        display: 'inline-flex', gap: 'var(--space-1)',
        padding: 'var(--space-1) var(--space-2)',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--color-surface-2)',
        border: '1px solid var(--color-border)',
      }}>
        {luces.map(l => (
          <span key={l} style={{
            width: '11px', height: '11px', borderRadius: 'var(--radius-pill)',
            background: l === semaforo ? encendida[l] : 'var(--color-border-2)',
            ...(l === semaforo ? { boxShadow: `0 0 0 2px var(--color-surface)` } : {}),
          }} />
        ))}
      </span>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
        color: _COLOR_SEMAFORO[semaforo] || 'var(--color-text-3)',
      }}>
        {titulo}
      </span>
    </div>
  )
}

/** Cómo le va: lo que hoy obliga a salir a otras tres pantallas. */
function ComoVa({ ficha }) {
  // ⚠️ La guarda es sobre `como_va`, no sobre `ficha`. Comprobar solo el objeto
  // de arriba y luego desestructurar lo de dentro es EXACTAMENTE la fragilidad
  // que encontré en CifrasLanding el 26-08… y que repetí aquí doce horas
  // después. Una respuesta parcial desmonta el árbol entero de React.
  if (!ficha?.como_va) {
    return (
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
        color: 'var(--color-text-3)',
      }}>
        Leyendo cómo va…
      </div>
    )
  }
  const { sla, ultima_lectura: lectura, corriendo, uso } = ficha.como_va

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: 'var(--space-5)',
    }}>
      <div>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
          letterSpacing: '0.11em', textTransform: 'uppercase',
          color: 'var(--color-text-3)',
        }}>
          SLA
        </div>
        <div style={{ marginTop: 'var(--space-1)' }}>
          <SemaforoSLA semaforo={sla.semaforo} titulo={sla.titulo} />
        </div>
        {sla.detalle && (
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
            color: 'var(--color-text-3)', marginTop: 'var(--space-05)',
          }}>
            {sla.detalle}
          </div>
        )}
      </div>
      <CampoFicha
        etiqueta="Última lectura"
        /* Sin lectura NO se pinta un cero ni una fecha inventada. */
        valor={lectura.cuando ? _fmtFechaHora(lectura.cuando) : '—'}
        pie={lectura.cuando ? 'vuelta de precios terminada' : 'aún sin ninguna vuelta correcta'}
        color={lectura.cuando ? 'var(--color-text)' : 'var(--color-text-3)'}
      />
      <CampoFicha
        etiqueta="Próxima vuelta"
        valor={sla.proximo_run ? _fmtFechaHora(sla.proximo_run) : '—'}
        pie={sla.frecuencia ? `precios · ${sla.frecuencia}` : 'sin programar'}
        color={sla.proximo_run ? 'var(--color-text)' : 'var(--color-text-3)'}
      />
      <CampoFicha
        etiqueta={`Uso · ${uso.ventana_dias} días`}
        valor={uso.accesos == null ? '—' : `${uso.accesos} accesos`}
        pie={uso.ultimo ? `último ${_fmtFechaHora(uso.ultimo)}` : 'sin accesos registrados'}
      />
      {corriendo && (
        <CampoFicha
          etiqueta="Corriendo ahora"
          valor={`${corriendo.tipo} · ${corriendo.minutos} min`}
          /* Tres estados y no dos: ha dejado de fichar (avería), tarda más de lo
             normal de SU tipo pero sigue vivo, o va bien. El «por encima del
             umbral» de antes era un umbral único de 40 min que un `precios` sano
             supera a diario — decía avería donde no la había. */
          pie={corriendo.colgado
            ? 'ha dejado de dar señales'
            : corriendo.tardando
              ? `tarda más de lo normal (su tipo suele estar bajo ${corriendo.techo_min} min)`
              : 'en marcha'}
          color={corriendo.colgado ? 'var(--color-error)'
            : corriendo.tardando ? 'var(--color-warn)' : 'var(--color-running)'}
        />
      )}
    </div>
  )
}

function _fmtFechaHora(iso) {
  return fechaHoraCL(iso, {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }) || '—'
}

// Las tres preguntas del rediseño anterior, ahora como pestañas, más las
// ejecuciones del cliente plegadas dentro (Manuel, 28-08): todo lo de un
// cliente vive en su ficha. La cabecera y el aviso de job colgado quedan
// SIEMPRE visibles, estés en la pestaña que estés.
const PESTANAS_FICHA = [
  { clave: 'como-va', label: 'Cómo va' },
  { clave: 'vigila', label: 'A quién vigila' },
  { clave: 'contrato', label: 'Contrato' },
  { clave: 'ejecuciones', label: 'Ejecuciones' },
  // Manuel, 04-09: «la pestaña ejecuciones ya ocupa demasiado contenido que, en
  // parte, no tiene que ver con las ejecuciones puras». Ejecuciones contesta
  // «¿qué está pasando ahora?»; Diagnóstico, «¿por qué los números son los que
  // son?». Se llama así, y no «Herramientas», porque el módulo Suscripciones ya
  // tiene una pestaña «Diagnóstico» con ese mismo significado: dos palabras
  // para lo mismo en dos módulos es la deuda del §4 en el vocabulario.
  { clave: 'diagnostico', label: 'Diagnóstico' },
]

// Las pestañas que se quedan con toda la pantalla. Las demás llevan tope de
// 960 px porque son formularios, y un formulario de 1.600 px no se lee.
const PESTANAS_ANCHAS = new Set(['ejecuciones', 'diagnostico'])

export default function ClienteDetalle() {
  const { id: clienteId } = useParams()
  const navigate = useNavigate()
  // Deep-link a una pestaña (?pestana=ejecuciones): es adonde redirige la
  // antigua /ejecuciones/:clienteId, y los enlaces guardados siguen vivos.
  const [searchParams] = useSearchParams()
  const pedida = searchParams.get('pestana')
  const [pestana, setPestana] = useState(
    PESTANAS_FICHA.some(p => p.clave === pedida) ? pedida : 'como-va'
  )
  const { data: cliente, isLoading, error } = useCliente(clienteId)
  const { data: ficha } = useFichaCliente(clienteId)
  const deleteMutation = useDeleteCliente()

  async function desactivar() {
    const ok = window.confirm(
      `¿Desactivar al cliente "${cliente.nombre}"?\n\n` +
      'Se detiene su scheduler y deja de aparecer en el listado. ' +
      'Sus datos se conservan (borrado suave).'
    )
    if (!ok) return
    try {
      await deleteMutation.mutateAsync(clienteId)
      navigate('/clientes')
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'No se pudo desactivar el cliente.')
    }
  }

  if (isLoading) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Cliente" />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>
        Cargando...
      </div>
    </div>
  )

  if (error || !cliente) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Cliente" />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-error)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>
        Cliente no encontrado
      </div>
    </div>
  )

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title={cliente.nombre}
        /* El correo va aquí por decisión de Manuel (26-08): si hay que
           contactar rápido con un cliente por soporte, no debe haber que
           buscarlo en otra pantalla. */
        subtitle={[cliente.convenio, cliente.email, cliente.cliente_id]
          .filter(Boolean).join(' · ')}
        action={
          <Button
            size="sm" variant="secondary"
            icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>}
            onClick={() => window.open(`${API_BASE}/data/${clienteId}/dashboard`, '_blank')}
          >
            Ver su dashboard
          </Button>
        }
      />

      {/* Si algo va mal con este cliente, se dice antes que nada y SOBRE las
          pestañas: se ve estés donde estés (Manuel, 28-08). Mismo principio
          que la pantalla Estado. */}
      {(ficha?.como_va?.corriendo?.colgado || ficha?.como_va?.corriendo?.tardando) && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'auto 1fr',
          gap: 'var(--space-4)', alignItems: 'baseline',
          background: ficha.como_va.corriendo.colgado
            ? 'var(--color-error-soft)' : 'var(--color-warn-soft)',
          padding: 'var(--space-4) var(--space-6)',
        }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: 'var(--radius-pill)',
            background: ficha.como_va.corriendo.colgado
              ? 'var(--color-error)' : 'var(--color-warn)',
            display: 'block', marginTop: '6px',
          }} />
          <div>
            <div style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-bold)' }}>
              Su job de {ficha.como_va.corriendo.tipo} lleva {ficha.como_va.corriendo.minutos} min corriendo
              {ficha.como_va.corriendo.colgado ? ' y ha dejado de dar señales' : ' y sigue vivo'}
            </div>
            <div style={{
              fontSize: 'var(--text-base)', color: 'var(--color-text-2)',
              marginTop: 'var(--space-1)', lineHeight: 1.5,
            }}>
              {ficha.como_va.corriendo.colgado
                ? 'Su proceso no ficha, así que lo que produce no va a llegar.'
                : `Para un job de su tipo lo normal queda por debajo de ${ficha.como_va.corriendo.techo_min} min, así que conviene mirarlo.`}
              {' '}Su dashboard sigue mostrando la lectura anterior.
            </div>
          </div>
        </div>
      )}

      <BarraPestanas pestanas={PESTANAS_FICHA} activa={pestana} onCambio={setPestana} />

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
        {/* El tope de 960px se levanta SOLO en Ejecuciones (Manuel, 31-08, 2ª
            ronda). Medido a 1920: la tarjeta ocupaba 958 px de los 1636
            disponibles y la tabla de la cola necesitaba 1022 — o sea que se
            salía 112 px por la derecha y lo que se caía del borde era justo la
            última columna, «Resultado». Sin el tope se lleva 724 px y la tabla
            deja de desbordar.

            Las otras tres pestañas lo CONSERVAN a propósito: Cómo va, A quién
            vigila y Contrato son formularios y texto seguido, que estirados a
            1600 px se leen peor, no mejor. */}
        {/* ⚠️ «Diagnóstico» hereda el ancho completo de «Ejecuciones», y no es
            un detalle: las dos tarjetas que se mudaron ahí en la ronda del
            04-09 son EXACTAMENTE las que motivaron quitar el tope de 960 px el
            31-08 (sus textos largos se ahogaban en 724 px). Mudarlas sin
            traerse la excepción habría deshecho aquel arreglo sin que nadie lo
            notara hasta volver a leer un diagnóstico. */}
        <div className="cm-ficha-cuerpo"
             style={{ maxWidth: PESTANAS_ANCHAS.has(pestana) ? 'none' : '960px' }}>

          {pestana === 'como-va' && (
            <Seccion rotulo="Cómo va" primera>
              <ComoVa ficha={ficha} />
              {ficha?.plan?.frase && (
                <div style={{
                  marginTop: 'var(--space-4)',
                  background: 'var(--color-highlight-soft)',
                  borderLeft: '3px solid var(--color-highlight)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3) var(--space-4)',
                  fontSize: 'var(--text-base)', color: 'var(--color-text)',
                  lineHeight: 1.55, maxWidth: '68ch',
                }}>
                  {ficha.plan.frase}
                </div>
              )}
              {/* Dónde factura de verdad. Va ANTES del uso del plan: el uso
                  se lee distinto según cuántas tiendas tenga el tenant. */}
              <div style={{ marginTop: 'var(--space-5)' }}>
                <SubRotulo>Dónde factura</SubRotulo>
                {/* Las dos acciones viajan para que los avisos de dentro citen
                    su pestaña y su rótulo de una sola fuente. Son DOS y no una
                    porque los avisos mandan a sitios distintos: el censo resuelve
                    lo pendiente de preguntar, la sonda lo que no tiene tienda. */}
                <TiendasDelTenant
                  clienteId={clienteId}
                  accionCenso={(ficha?.acciones || []).find(a => a.clave === 'censo')}
                  accionSonda={(ficha?.acciones || []).find(a => a.clave === 'sonda')} />
              </div>
              <div style={{ marginTop: 'var(--space-5)' }}>
                <UsoTab clienteId={clienteId} />
              </div>
            </Seccion>
          )}

          {pestana === 'vigila' && (
            <Seccion primera rotulo={`A quién vigila · ${cliente.empresas_propias?.length ?? 0} propias, ${cliente.competidores?.length ?? 0} competidores`}>
              <SubRotulo>Sus empresas</SubRotulo>
              <EmpresasTab empresas={cliente.empresas_propias ?? []} clienteId={clienteId} />
              <div style={{ marginTop: 'var(--space-6)' }}>
                <SubRotulo>A quién compara</SubRotulo>
                <CompetidoresTab competidores={cliente.competidores ?? []} clienteId={clienteId} />
              </div>
            </Seccion>
          )}

          {pestana === 'contrato' && (
            <>
              {/* Márgenes apretados a propósito (Manuel, 28-08): la pestaña
                  entera tiene que caber en una pantalla sin scroll. */}
              <Seccion rotulo="Qué tiene contratado" primera>
                <InfoTab cliente={cliente} />
                <ContactoUsuarios cliente={cliente} ficha={ficha} clienteId={clienteId} />
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <ConcesionSeccion cliente={cliente} clienteId={clienteId} />
                </div>
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <SchedulerTab scheduler={cliente.scheduler_config} clienteId={clienteId} />
                </div>
              </Seccion>

              {/* La zona de riesgo es del contrato: corta el servicio o el
                  cobro. Al pie y plegada, como siempre. */}
              <div style={{
                marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)',
                borderTop: '1px solid var(--color-border-2)',
              }}>
                <ZonaRiesgo
                  cliente={cliente} clienteId={clienteId}
                  onDesactivar={desactivar} desactivando={deleteMutation.isPending}
                />
              </div>
            </>
          )}

          {pestana === 'ejecuciones' && (
            <EjecucionesCliente clienteId={clienteId} />
          )}

          {/* Sin rótulo de sección, igual que Ejecuciones: sus dos tarjetas ya
              llevan título propio y la pestaña ya dice dónde estás. */}
          {pestana === 'diagnostico' && (
            <DiagnosticoCliente clienteId={clienteId} />
          )}
        </div>
      </div>
    </div>
  )
}
